//#region imports
import moment from 'moment-timezone';
import i18next from 'i18next';
import { Box, color, Stack } from '@mui/system';
import { useTour } from '@reactour/tour';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  DataGridPro,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
  GridFilterInputValue
} from '@mui/x-data-grid-pro';
import {
  Chip,
  Dialog,
  Tooltip,
  IconButton,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Link,
  CircularProgress,
  Typography,
} from '@mui/material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import {
  DeleteOutlineOutlined as DeleteIcon,
  DownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import {
  faFilePen,
  faCircleInfo,
  faFolderArrowDown,
  faPaperPlane,
  faCheck,
  faClose,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faAdd,
  fas,
  faCalendarPlus,
  faBan,
  faChalkboardUser,
  faHandshakeAngle,
  faStreetView,
  faScrewdriverWrench,
  faFlagCheckered,
  faSquarePlus,
  faPersonCirclePlus,
  faXmark,
  faThumbsUp,
  faSkullCrossbones,
  faPersonWalkingArrowRight,
  faCheckDouble,
} from '@fortawesome/free-solid-svg-icons';

import ModalV from '../Modal/Create/ModalV';
import customAxios from '../../utils/customAxios';
import { GlobalVariables } from '../../globalVar';
import useVisitsTour from '../../tours/visitsTour';
import EditReportModal from '../Modal/EditReportModal';
import ModalVDelete from '../Modal/Delete/ModalVDelete';
import ReportViewer from '../ReportsBuilder/ReportViewer';
import { useTableLocale } from '../../hooks/useTableLocale';
import VisitSubvisits from '../MasterDetails/VisitSubvisits';
import { useDownloadReport } from '../../hooks/UseDownloadReport';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import ModalVMultDelete from '../Modal/Delete/Multiple/ModalVMultDelete';
import useFetchTemplateAndInputs from '../../hooks/UseFetchTemplateAndInputs';
import { useUserPermissions } from '../../hooks/userPermissions';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import SendVisitModal from '../Modal/Sigma/Visits/SendVisitModal';
import UpdateVisitModal from '../Modal/Sigma/Visits/UpdateVisitModal';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDataGridLocalStorage } from '../../hooks/UseDataGridLocalStorage';
import { AutocompleteFilterInput } from '../Utils/AutocompleteFilterInput';
import { getToast } from '../../utils/toastConfig';

library.add(
  fas,
  faCalendarPlus,
  faBan,
  faChalkboardUser,
  faHandshakeAngle,
  faStreetView,
  faScrewdriverWrench,
  faFlagCheckered,
  faSquarePlus,
  faPersonCirclePlus,
  faXmark,
  faThumbsUp,
  faSkullCrossbones,
  faPersonWalkingArrowRight,
  faCheckDouble
);
//#endregion

const LOCAL_COLUMN_WIDTH = GlobalVariables.getDefaultColumnWidth();

// Orden requerido para el rol 1023 READ ONLY (estable a nivel módulo)
const ROLE_1023_FIELDS_ORDER = [
  'id', // Ticket SIGMA
  'oxxo_ticket', // Ticket
  'final_status_name', // Estatus final
  'month', // Mes
  'region_name', // Región
  'ticket_date', // Fecha y hora del ticket
  'cr', // CR
  'site_name', // Sucursal
  'business_name', // Plaza
  'failure_type_name', // Tipo de servicio
  'failure_level', // Nivel
  'failure_hour', // Hora
  'on_time', // ¿A tiempo?
  'failure_note', // Comentario del servicio (se asegura en readonly)
  'job_start_date_manual', // Fecha y hora de inicio de trabajos
  'job_end_date_manual', // Fecha y hora de cierre
  'close_comment', // Comentario de cierre
  'tech_name', // Técnico asignado
  'client_ti', // Ti del cliente
  'created_by_name', // Creado por
];

// Function to calculate gradient color based on SLA value
const getGradientColor = (sla) => {
  const green = [0, 255, 0]; // RGB for green
  const red = [255, 0, 0]; // RGB for red

  // Calculate interpolation factor
  const factor = sla / 100;

  // Interpolate between green and red
  const r = Math.round(green[0] + factor * (red[0] - green[0]));
  const g = Math.round(green[1] + factor * (red[1] - green[1]));
  const b = Math.round(green[2] + factor * (red[2] - green[2]));

  return `rgb(${r}, ${g}, ${b})`;
};

// (Eliminado: AutocompleteFilterInputLocal no usado)

const containsAnyOperator = {
  label: 'contiene (separado por comas)',
  value: 'containsAny',
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.value) return null;
    const searchTerms = filterItem.value
      .split(',')
      .map((term) => term.trim().toLowerCase())
      .filter((term) => term.length > 0);

    return (value) => {
      if (!value) return false;
      const cellValue = String(value).toLowerCase();
      return searchTerms.some((term) => cellValue.includes(term));
    };
  },
  InputComponent: GridFilterInputValue,
  InputComponentProps: { type: 'text' },
};

export const VisitsTableAxios = () => {
  const apiRef = useGridApiRef();

  //region state (persisted grid state)
  const {
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    paginationModel,
    setPaginationModel,
    columnOrder,
    columnSizes,
    columnVisibilityModel,
    setColumnVisibilityModel,
  } = useDataGridLocalStorage(apiRef, 'service-table-settings');
  //endregion

  //#region state (local)
  const [shouldApplyInitialFilter, setShouldApplyInitialFilter] =
    useState(true);
  const [visitData, setVisitData] = useState([]);
  const [vSubvisitsData, setVSubvisitsData] = useState([]);
  const [parentVisit, setParentVisit] = useState(null);
  const [visitId, setVisitId] = useState('');
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(true);
  const [modalVShow, setModalVShow] = useState(false);
  const [modalVDeleteShow, setModalVDeleteShow] = useState(false);
  const [showSendVModal, setShowSendVModal] = useState(false);
  const [modalVMultipleDeleteShow, setModalVMultipleDeleteShow] =
    useState(false);
  const [showEditReportModal, setShowEditReportModal] = useState(false);
  const [showEditVisitModal, setShowEditVisitModal] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  //#endregion

  //#region hooks/derived
  const navigate = useNavigate();
  const { downloadReport } = useDownloadReport();
  const userPermissions = useUserPermissions();
  const { t } = useTranslation();
  const {
    visitsCRUDStr,
    multDeleteString,
    addString,
    tableActionsString,
    dwQResponsesString,
    logsString,
    deleteString,
    noVisitsString,
    viewTutorialString,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
    editString,
  } = useTranslationHook();
  const { setIsOpen, setSteps, setCurrentStep } = useTour();
  const visitsTour = useVisitsTour(setModalVShow);
  const {
    showReportViewer,
    reportTemplate,
    reportInputs,
    fetchTemplateAndInputs,
    closeReportViewer,
  } = useFetchTemplateAndInputs();
  const locale = useTableLocale();
  const userIdForDVisits = localStorage.getItem('userIdForDVisits');
  const currUserRol = localStorage.getItem('currUserRol');
  //#endregion

  //#region effects
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('visitsTourViewed');
    if (!hasSeenTour) {
      setIsOpen(true);
      setSteps(visitsTour);
      setCurrentStep(0);
      localStorage.setItem('visitsTourViewed', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsOpen, setSteps]);

  useEffect(() => {
    if (currUserRol !== '1023') return;
    setColumnVisibilityModel((prev) => ({
      ...prev,
    }));
  }, [currUserRol, setColumnVisibilityModel]);

  useEffect(() => {
    const applyInitialFilter = () => {
      if (filterModel?.items?.length > 0) {
        handleFilterModelChange(filterModel);
      }
    };
    window.addEventListener('force-filter-load', applyInitialFilter);
    return () =>
      window.removeEventListener('force-filter-load', applyInitialFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModel, visitData]);

  useEffect(() => {
    const updateVisitData = () => {
      setVisitData((prevData) => {
        return prevData.map((visit) => ({
          ...visit,
          is_active: t(`${visit.is_active}`),
        }));
      });
    };
    i18next.on('languageChanged', updateVisitData);
    return () => {
      i18next.off('languageChanged', updateVisitData);
    };
  }, [t]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion

  //#region handlers/helpers
  const restartTour = () => {
    setIsOpen(true);
    setSteps(visitsTour);
    setCurrentStep(0);
    localStorage.setItem('visitsTourViewed', 'false');
  };

  const getData = async () => {
    setIsLoading(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}listVisits/${userIdForDVisits}`;
      const response = await customAxios.post(
        endpoint,
        {
          filters: filterModel,
          pagination: {
            rowsPerPage: paginationModel.pageSize,
            page: paginationModel.page + 1,
          },
          columns:
            currUserRol === '1023'
              ? { ...columnVisibilityModel }
              : columnVisibilityModel,
          columnOrder,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );
      const visits = response.data.data.visits.map((visit) => ({
        ...visit,
        is_active: t(`${visit.is_active}`),
      }));
      setVisitData(visits);
      setRowCount(response.data.data.totalRows);
      setVSubvisitsData(visits.flatMap((visit) => visit.subvisits));
      setIsLoading(false);

      if (shouldApplyInitialFilter && filterModel?.items?.length > 0) {
        await handleFilterModelChange(filterModel);
        setShouldApplyInitialFilter(false);
      }
    } catch (error) {
      console.log('Error al cargar visitas:', error);
      setVisitData([]);
      setVSubvisitsData([]);
      setRowCount(0);
      setIsLoading(false);
    }
  };

  const manageVisitDelete = (visitInfo) => {
    localStorage.setItem('visitIdToDelete', visitInfo.id);
    localStorage.setItem('visitSiteNameToDelete', visitInfo.site_name);
    localStorage.setItem('visitTypeNameToDelete', visitInfo.vt_name);
    localStorage.setItem('visitTechToDelete', visitInfo.name);
    localStorage.setItem('visitUpdatedDateToDelete', visitInfo.updated_at);
    localStorage.setItem('visitDateToDelete', visitInfo.visit_date);
  };

  const manageVisitMultipleDelete = () => {
    localStorage.setItem('visitMultipleDeleteList', rowSelectionModel);
  };

  const manageVisitReportDW = (visitInfo) => {
    const visitId = visitInfo.id;
    setVisitId(visitId);
    fetchTemplateAndInputs(visitId);
  };

  const manageVisitMAssetDW = (visitInfo) => {
    const visitId = visitInfo.id;
    downloadReport(visitId, true);
  };

  const handleFilterModelChange = async (newFilterModel) => {
    setFilterModel(newFilterModel);
    setIsLoading(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}listVisits/${userIdForDVisits}`;
      const response = await customAxios.post(
        endpoint,
        {
          filters: newFilterModel,
          pagination: {
            rowsPerPage: paginationModel.pageSize,
            page: paginationModel.page + 1,
          },
          columns:
            currUserRol === '1023'
              ? { ...columnVisibilityModel }
              : columnVisibilityModel,
          columnOrder,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );
      setVisitData(response.data.data.visits);
      setRowCount(response.data.data.totalRows);
    } catch (error) {
      console.log('🚀 ~ error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageModelChange = async (paginationModel) => {
    setPaginationModel(paginationModel);
    setIsLoading(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}listVisits/${userIdForDVisits}`;
      const response = await customAxios.post(
        endpoint,
        {
          filters: filterModel,
          pagination: {
            rowsPerPage: paginationModel.pageSize,
            page: paginationModel.page + 1,
          },
          columns:
            currUserRol === '1023'
              ? { ...columnVisibilityModel }
              : columnVisibilityModel,
          columnOrder,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );
      setVisitData(response.data.data.visits);
      setRowCount(response.data.data.totalRows);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterColumns = ({ field, columns, currentFilters }) => {
    const filteredFields = currentFilters?.map((item) => item.field);
    return columns
      .filter((colDef) => {
        if (!colDef.filterable) return false;
        if (colDef.field === 'business_name') {
          return (
            colDef.field === field || !filteredFields.includes('business_name')
          );
        }
        return true;
      })
      .map((column) => column.field);
  };

  const getColumnForNewFilter = ({ currentFilters, columns }) => {
    const filteredFields = currentFilters?.map(({ field }) => field);
    const columnForNewFilter = columns
      .filter((colDef) => {
        if (!colDef.filterable) return false;
        if (colDef.field === 'business_name') {
          return !filteredFields.includes('business_name');
        }
        return true;
      })
      .find((colDef) => colDef.filterOperators?.length);
    return columnForNewFilter?.field ?? null;
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    getToast({ status: 'success', title: 'Descargando archivo...' });
    const api = apiRef.current;
    if (!api || typeof api.getAllColumns !== 'function') {
      console.warn('⛔ apiRef not initialized yet.');
      setIsExporting(false);
      return;
    }
    const visibleColumns = api.getVisibleColumns();
    const fullVisibilityModel = {};
    visibleColumns.forEach((col) => {
      fullVisibilityModel[col.field] = true;
    });
    const fullColumnOrder = api.getAllColumns().map((col) => col.field);

    try {
      const endpoint = `${GlobalVariables.getURL()}listVisits/${userIdForDVisits}?export=true`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
        body: JSON.stringify({
          filters: filterModel,
          columns: fullVisibilityModel,
          columnOrder: fullColumnOrder,
        }),
      });

      if (!response.ok) {
        getToast({ status: 'error', title: 'Error al descargar el archivo' });
        return;
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const match = contentDisposition?.match(/filename="(.+)"/);
      const filename =
        match?.[1] ||
        `export-servicios_${new Date().toISOString().split('T')[0]}.xlsx`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      getToast({
        status: 'success',
        title: 'Archivo descargado correctamente',
      });
    } catch (err) {
      console.error('Download failed:', err);
      getToast({
        status: 'error',
        title: err.response?.data?.message || 'Error exporting data',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getDetailPanelContent = useCallback(
    ({ row }) => {
      if (Array.isArray(row.subvisits) && row.subvisits.length >= 1) {
        return (
          <VisitSubvisits
            row={row}
            subvisitData={vSubvisitsData.filter(
              (subvisit) => subvisit.parent_id === parentVisit
            )}
            visitColumns={finalColumns}
            actionColumn={actionColumn}
            isFiltering={isFiltering}
            setIsFiltering={setIsFiltering}
            setParentVisit={setParentVisit}
          />
        );
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentVisit, isFiltering]
  );

  const getDetailPanelHeight = useCallback(() => 220, []);
  //#endregion

  //#region columns
  const dwAllReportsString = 'Descargar todos los reportes';

  const actionColumn = [
    {
      filterable: false,
      field: 'action',
      headerName: tableActionsString,
      headerClassName: 'visits-datagrid-actions-tour',
      width: 200,
      renderCell: (params) => {
        return (
          <div
            className='cellAction visits-datagrid-actions-tour'
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tooltip title={logsString} arrow>
              <button
                style={{ background: 'none', border: 'none', padding: 0 }}
                onClick={() => {
                  navigate(`/visits/details/${params.row.id}`);
                }}
              >
                <VisibilityOutlinedIcon
                  className='detail-icons'
                  id='update-icon'
                />
              </button>
            </Tooltip>

            {params.row.visit_status_id === 7 ? (
              <>
                {params.row.asset_name !== 'Multiple' ? (
                  <>
                    {userPermissions.includes('Editar Reportes') && (
                      <Tooltip title='Editar reporte' arrow>
                        <IconButton
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                          }}
                          onClick={() => {
                            setVisitId(params.id);
                            setShowEditReportModal(true);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faFilePen}
                            className='detail-icons'
                            id='edit-report-icon'
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={dwQResponsesString} arrow>
                      <IconButton
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                        }}
                        onClick={() => manageVisitReportDW(params.row)}
                      >
                        <DownloadIcon
                          className='detail-icons'
                          id='update-icon'
                        />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : null}
                {params.row.asset_name === 'Multiple' ? (
                  <Tooltip title={dwAllReportsString} arrow>
                    <IconButton
                      style={{ background: 'none', border: 'none', padding: 0 }}
                      onClick={() => manageVisitMAssetDW(params.row)}
                    >
                      <FontAwesomeIcon
                        icon={faFolderArrowDown}
                        className='detail-icons'
                        id='dwall-icon'
                      />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </>
            ) : null}

            {(params.row.visit_status_id === 2 ||
              params.row.visit_status_id === 3 ||
              params.row.visit_status_id === 7 ||
              params.row.final_status_id === 10 ||
              params.row.final_status_id === 14 ||
              params.row.final_status_id === 5) &&
            userPermissions.includes('Update Visit') ? (
              <Tooltip title={editString} arrow>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    marginTop: '5px',
                  }}
                  onClick={() => {
                    setVisitId(params.id);
                    setShowEditVisitModal(true);
                  }}
                >
                  <ModeEditOutlineOutlinedIcon
                    className='detail-icons'
                    id='update-icon'
                  />
                </button>
              </Tooltip>
            ) : null}

            {params.row.final_status_id === 1 ? (
              <Tooltip title={'Enviar'} arrow>
                <IconButton
                  style={{ background: 'none', border: 'none', padding: 0 }}
                  onClick={() => {
                    setVisitId(params.id);
                    setShowSendVModal(true);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faPaperPlane}
                    className='detail-icons'
                    id='send-visit-icon'
                  />
                </IconButton>
              </Tooltip>
            ) : null}

            <Tooltip title={deleteString} arrow>
              <IconButton
                style={{ background: 'none', border: 'none', padding: 0 }}
                onClick={() => {
                  setModalVDeleteShow(true);
                  manageVisitDelete(params.row);
                }}
              >
                <DeleteIcon className='detail-icons' id='delete-icon' />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  // baseColumns sin cálculos de ancho dinámico
  const baseColumns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'Ticket',
        minWidth: 120,
        filterable: true,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        filterOperators: getGridNumericOperators().filter(
          (operator) => operator.value !== 'isAnyOf'
        ),
        renderCell: (params) => {
          // Return the raw value as a string to avoid MUI numeric formatting (commas)
          return params.value != null ? String(params.value) : '';
        },
      },
      {
        field: 'resend_from',
        headerName: 'Ticket original',
        flex: 1,
        renderCell: (params) => {
          const handleResendVisitClick = () => {
            navigate(`/visits/details/${params.row.resend_from}`);
          };

          return (
            <div>
              {params.row.resend_from ? (
                <Link
                  sx={{ cursor: 'pointer' }}
                  onClick={handleResendVisitClick}
                >
                  {params.row.resend_from}
                </Link>
              ) : (
                <span>-</span>
              )}
            </div>
          );
        },
        filterable: true,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        filterOperators: getGridNumericOperators().filter(
          (operator) => operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'oxxo_ticket',
        headerName: 'Ticket',
        minWidth: 120,
        filterable: true,
        type: 'string',
        filterOperators: [...getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'contains' &&
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ), containsAnyOperator],
      },
      {
        field: 'final_status_name',
        headerName: 'Estatus final',
        minWidth: 160,
        filterable: true,
        type: 'singleSelect',
        valueOptions: [
          'Pendiente por Enviar',
          'Enviado',
          'Pendiente',
          'Rechazado',
          'Cancelado',
          'Completo',
          'Reenviado',
          'Incompleto',
          'Cancelado por Reenvío',
        ],
        renderCell: (params) => {
          const status = params.value;
          let color = 'default';
          let label = status;

          switch (status) {
            case 'Enviado':
              color = 'success';
              break;
            case 'Reenviado':
              color = 'info';
              break;
            case 'Completo':
              color = 'primary';
              break;
            case 'Pendiente por enviar':
              color = 'warning';
              break;
            case 'Incompleto':
              color = 'error';
              break;
            default:
              color = 'default';
          }

          return (
            <Chip
              label={label}
              color={color}
              size='small'
              variant='outlined'
              sx={{ fontWeight: 'bold', minWidth: 120 }}
            />
          );
        },
      },
      {
        field: 'year',
        headerName: 'Año',
        minWidth: 80,
        renderCell: (params) => {
          const date = moment(
            params.row.created_at,
            'D [de] MMM. [de] YYYY HH:mm'
          );
          return date.year();
        },
        filterable: true,
        type: 'number',
        filterOperators: getGridNumericOperators().filter(
          (operator) => operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'month',
        headerName: 'Mes',
        minWidth: 120,
        renderCell: (params) => {
          const date = moment(
            params.row.created_at,
            'D [de] MMM. [de] YYYY HH:mm'
          );
          return date.format('MMMM').toUpperCase();
        },
        filterable: true,
        type: 'singleSelect',
        valueOptions: [
          'ENERO',
          'FEBRERO',
          'MARZO',
          'ABRIL',
          'MAYO',
          'JUNIO',
          'JULIO',
          'AGOSTO',
          'SEPTIEMBRE',
          'OCTUBRE',
          'NOVIEMBRE',
          'DICIEMBRE',
        ],
      },
      {
        field: 'region_name',
        headerName: 'Región',
        minWidth: 140,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Región'
                  endpoint={`${GlobalVariables.getURL()}region`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Plaza'
                  endpoint={`${GlobalVariables.getURL()}region`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Región'
                  endpoint={`${GlobalVariables.getURL()}region`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
      {
        field: 'city',
        headerName: 'Ciudad',
        minWidth: 120,
        filterable: true,
        type: 'string',
        filterOperators: getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'kmnivel',
        headerName: 'KM Nivel',
        minWidth: 130,
        filterable: true,
        type: 'string',
        filterOperators: getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'ticket_date',
        headerName: 'Fecha y hora del ticket',
        minWidth: 180,
        filterable: true,
        type: 'dateTime',
        valueGetter: ({ value }) => {
          const momentDate = moment(value, 'D [de] MMM. [de] YYYY HH:mm');
          return momentDate.toDate();
        },
        renderCell: (params) => params.row.ticket_date,
        filterOperators: [
          ...getGridDateOperators(),
          {
            value: 'between',
            label: 'entre',
            InputComponent: ({ item, applyValue }) => {
              const [dateRanges, setDateRanges] = useState(
                item.value
                  ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                  : [null, null]
              );
              const handleRangeChange = (newValue) => {
                const formattedRange = newValue.map((date) =>
                  date ? moment(date).format('YYYY-MM-DD') : null
                );
                setDateRanges(formattedRange);
                applyValue({ ...item, value: formattedRange });
              };
              return (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={moment.locale()}
                >
                  <DateRangePicker
                    label='Seleccionar rango de fechas'
                    value={dateRanges.map((date) =>
                      date ? moment(date) : null
                    )}
                    onChange={handleRangeChange}
                    localeText={{ start: 'Inicio', end: 'Fin' }}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />
                </LocalizationProvider>
              );
            },
          },
        ],
      },
      {
        field: 'commitment_date',
        headerName: 'Vencimiento original',
        minWidth: 160,
        filterable: true,
        type: 'dateTime',
        valueGetter: ({ value }) => {
          const momentDate = moment(value, 'D [de] MMM. [de] YYYY HH:mm');
          return momentDate.toDate();
        },
        renderCell: (params) => params.row.commitment_date,
        filterOperators: [
          ...getGridDateOperators(),
          {
            value: 'between',
            label: 'entre',
            InputComponent: ({ item, applyValue }) => {
              const [dateRanges, setDateRanges] = useState(
                item.value
                  ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                  : [null, null]
              );
              const handleRangeChange = (newValue) => {
                const formattedRange = newValue.map((date) =>
                  date ? moment(date).format('YYYY-MM-DD') : null
                );
                setDateRanges(formattedRange);
                applyValue({ ...item, value: formattedRange });
              };
              return (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={moment.locale()}
                >
                  <DateRangePicker
                    label='Seleccionar rango de fechas'
                    value={dateRanges.map((date) =>
                      date ? moment(date) : null
                    )}
                    onChange={handleRangeChange}
                    localeText={{ start: 'Inicio', end: 'Fin' }}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />
                </LocalizationProvider>
              );
            },
          },
        ],
      },
      {
        field: 'last_commitment_date',
        headerName: 'Vencimiento',
        minWidth: 160,
        filterable: true,
        type: 'dateTime',
        valueGetter: ({ value }) => {
          const momentDate = moment(value, 'D [de] MMM. [de] YYYY HH:mm');
          return momentDate.toDate();
        },
        renderCell: (params) => params.row.last_commitment_date,
        filterOperators: [
          ...getGridDateOperators(),
          {
            value: 'between',
            label: 'entre',
            InputComponent: ({ item, applyValue }) => {
              const [dateRanges, setDateRanges] = useState(
                item.value
                  ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                  : [null, null]
              );
              const handleRangeChange = (newValue) => {
                const formattedRange = newValue.map((date) =>
                  date ? moment(date).format('YYYY-MM-DD') : null
                );
                setDateRanges(formattedRange);
                applyValue({ ...item, value: formattedRange });
              };
              return (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={moment.locale()}
                >
                  <DateRangePicker
                    label='Seleccionar rango de fechas'
                    value={dateRanges.map((date) =>
                      date ? moment(date) : null
                    )}
                    onChange={handleRangeChange}
                    localeText={{ start: 'Inicio', end: 'Fin' }}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />
                </LocalizationProvider>
              );
            },
          },
        ],
      },
      {
        field: 'last_stop_date',
        headerName: 'Fecha y hora de paro/reenvío',
        minWidth: 180,
        filterable: true,
        type: 'date',
        valueGetter: ({ value }) => {
          const momentDate = moment(value, 'D [de] MMM. [de] YYYY HH:mm');
          return momentDate.toDate();
        },
        renderCell: (params) => params.row.last_stop_date,
        filterOperators: [
          ...getGridDateOperators(),
          {
            value: 'between',
            label: 'entre',
            InputComponent: ({ item, applyValue }) => {
              const [dateRanges, setDateRanges] = useState(
                item.value
                  ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                  : [null, null]
              );
              const handleRangeChange = (newValue) => {
                const formattedRange = newValue.map((date) =>
                  date ? moment(date).format('YYYY-MM-DD') : null
                );
                setDateRanges(formattedRange);
                applyValue({ ...item, value: formattedRange });
              };
              return (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={moment.locale()}
                >
                  <DateRangePicker
                    label='Seleccionar rango de fechas'
                    value={dateRanges.map((date) =>
                      date ? moment(date) : null
                    )}
                    onChange={handleRangeChange}
                    localeText={{ start: 'Inicio', end: 'Fin' }}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />
                </LocalizationProvider>
              );
            },
          },
        ],
      },
      {
        field: 'sla_percentage',
        headerName: '% Transcurrido',
        minWidth: 110,
        align: 'center',
        filterable: false,
        renderCell: (params) => {
          const slaNum = Number(params.row.sla_percentage) || 0;
          const slaFixed = slaNum.toFixed(2);
          const color = getGradientColor(slaNum);
          return (
            <div style={{ color: color, fontWeight: 'bold' }}>
              <span>{slaFixed}%</span>
            </div>
          );
        },
      },
      {
        field: 'remaining_timestamp',
        headerName: 'Hrs / Min Restantes',
        minWidth: 140,
        align: 'center',
        filterable: false,
      },
      {
        field: 'cr',
        headerName: 'CR',
        minWidth: 100,
        filterable: true,
        type: 'string',
        filterOperators: getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'site_name',
        headerName: 'Sucursal',
        minWidth: 200,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Sucursal'
                  endpoint={`${GlobalVariables.getURL()}getAutocompleteSites`}
                  dataPath='data'
                  idKey='id'
                  labelKey='site_name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Sucursal'
                  endpoint={`${GlobalVariables.getURL()}getAutocompleteSites`}
                  dataPath='data'
                  idKey='id'
                  labelKey='site_name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Sucursal'
                  endpoint={`${GlobalVariables.getURL()}getAutocompleteSites`}
                  dataPath='data'
                  idKey='id'
                  labelKey='site_name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
      {
        field: 'site_priority',
        headerName: '¿Sucursal prioritaria?',
        minWidth: 140,
        type: 'singleSelect',
        filterOperators: [
          {
            value: 'equals',
            getApplyFilterFn: (filterItem) => {
              if (!filterItem) return null;
              return (params) =>
                String(params.value) === String(filterItem.value);
            },
            InputComponent: ({ item, applyValue }) => {
              const handleChange = (event) => {
                applyValue({ ...item, value: event.target.value });
              };
              return (
                <FormControl fullWidth>
                  <InputLabel id='is_priority_select-label'>
                    Seleccionar...
                  </InputLabel>
                  <Select
                    labelId='is_priority_select-label'
                    label='Seleccionar...'
                    value={item.value || ''}
                    onChange={handleChange}
                    size='medium'
                    variant='standard'
                  >
                    <MenuItem value='1'>Sí</MenuItem>
                    <MenuItem value='0'>No</MenuItem>
                  </Select>
                </FormControl>
              );
            },
          },
          {
            value: 'not',
            label: 'no es',
            getApplyFilterFn: (filterItem) => {
              if (!filterItem) return null;
              return (params) =>
                String(params.value) !== String(filterItem.value);
            },
            InputComponent: ({ item, applyValue }) => {
              const handleChange = (event) => {
                applyValue({ ...item, value: event.target.value });
              };
              return (
                <FormControl fullWidth>
                  <InputLabel id='is_priority_not_select-label'>
                    Seleccionar...
                  </InputLabel>
                  <Select
                    labelId='is_priority_not_select-label'
                    label='Seleccionar...'
                    value={item.value || ''}
                    onChange={handleChange}
                    size='medium'
                    variant='standard'
                  >
                    <MenuItem value='1'>Sí</MenuItem>
                    <MenuItem value='0'>No</MenuItem>
                  </Select>
                </FormControl>
              );
            },
          },
        ],
      },
      {
        field: 'business_name',
        headerName: 'Plaza',
        minWidth: 180,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Plaza'
                  endpoint={`${GlobalVariables.getURL()}getAutocompleteClients`}
                  dataPath='data'
                  idKey='id'
                  labelKey='business_name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Plaza'
                  endpoint={`${GlobalVariables.getURL()}getAutocompleteClients`}
                  dataPath='data'
                  idKey='id'
                  labelKey='business_name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Plaza'
                  endpoint={`${GlobalVariables.getURL()}getAutocompleteClients`}
                  dataPath='data'
                  idKey='id'
                  labelKey='business_name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
      {
        field: 'failure_type_name',
        headerName: 'Tipo de servicio',
        minWidth: 160,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Tipo de servicio'
                  endpoint={`${GlobalVariables.getURL()}failure-types`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Tipo de servicio'
                  endpoint={`${GlobalVariables.getURL()}failure-types`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Tipo de servicio'
                  endpoint={`${GlobalVariables.getURL()}failure-types`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
      {
        field: 'failure_level',
        headerName: 'Nivel',
        minWidth: 110,
        filterable: true,
        type: 'number',
        filterOperators: getGridNumericOperators().filter(
          (operator) => operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'failure_hour',
        headerName: 'Hora',
        minWidth: 100,
        filterable: true,
        type: 'number',
        filterOperators: getGridNumericOperators().filter(
          (operator) => operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'on_time',
        headerName: '¿A tiempo?',
        minWidth: 120,
        filterable: true,
        type: 'singleSelect',
        filterOperators: [
          {
            value: 'equals',
            getApplyFilterFn: (filterItem) => {
              if (!filterItem?.value) return null;
              return (params) =>
                String(params.value) === String(filterItem.value);
            },
            InputComponent: ({ item, applyValue }) => {
              const handleChange = (event) => {
                applyValue({ ...item, value: event.target.value });
              };
              return (
                <FormControl fullWidth variant='standard'>
                  <InputLabel id='on_time_select-label'>
                    Seleccionar...
                  </InputLabel>
                  <Select
                    labelId='on_time_select-label'
                    value={item.value || ''}
                    onChange={handleChange}
                    label='Seleccionar...'
                    size='medium'
                  >
                    <MenuItem value='Sí'>Sí</MenuItem>
                    <MenuItem value='No'>No</MenuItem>
                  </Select>
                </FormControl>
              );
            },
          },
          {
            value: 'not',
            label: 'no es',
            getApplyFilterFn: (filterItem) => {
              if (!filterItem?.value) return null;
              return (params) =>
                String(params.value) !== String(filterItem.value);
            },
            InputComponent: ({ item, applyValue }) => {
              const handleChange = (event) => {
                applyValue({ ...item, value: event.target.value });
              };
              return (
                <FormControl fullWidth variant='standard'>
                  <InputLabel id='on_time_not_select-label'>
                    Seleccionar...
                  </InputLabel>
                  <Select
                    labelId='on_time_not_select-label'
                    value={item.value || ''}
                    onChange={handleChange}
                    label='Seleccionar...'
                    size='medium'
                  >
                    <MenuItem value='Sí'>Sí</MenuItem>
                    <MenuItem value='No'>No</MenuItem>
                  </Select>
                </FormControl>
              );
            },
          },
        ],
        renderCell: (params) => {
          const onTime = params.row.on_time;
          if (onTime === null || onTime === undefined || onTime === '')
            return '-';

          if (onTime === 'Sí') {
            return (
              <Tooltip title='Sí'>
                <FontAwesomeIcon icon={faCheck} color='green' fontSize='16px' />
              </Tooltip>
            );
          }

          if (onTime === 'No') {
            return (
              <Tooltip title='No'>
                <FontAwesomeIcon icon={faClose} color='red' fontSize='16px' />
              </Tooltip>
            );
          }

          return '-';
        },
      },
      // {
      //   field: 'on_time_manual',
      //   headerName: '¿A tiempo (manual)?',
      //   minWidth: 170,
      //   type: 'singleSelect',
      //   valueOptions: ['Sí', 'No'],
      //   renderCell: (params) => params.row.on_time_manual ?? '—',
      //   filterable: true,
      //   filterOperators: getGridStringOperators().filter((op) =>
      //     ['equals', 'isAnyOf', 'isEmpty', 'isNotEmpty'].includes(op.value)
      //   ),
      // },
      {
        field: 'client_ti',
        headerName: 'Ti del cliente',
        minWidth: 140,
        filterable: true,
        type: 'string',
        filterOperators: getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'category_name',
        headerName: 'Categoría',
        minWidth: 140,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Categoría'
                  endpoint={`${GlobalVariables.getURL()}categories`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Categoría'
                  endpoint={`${GlobalVariables.getURL()}categories`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Categoría'
                  endpoint={`${GlobalVariables.getURL()}categories`}
                  dataPath='data.data'
                  idKey='id'
                  labelKey='name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
      {
        field: 'vt_name',
        headerName: 'Actividad',
        minWidth: 160,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Actividad'
                  endpoint={`${GlobalVariables.getURL()}listVisitTypes`}
                  dataPath='data'
                  idKey='id'
                  labelKey='vt_name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Actividad'
                  endpoint={`${GlobalVariables.getURL()}listVisitTypes`}
                  dataPath='data'
                  idKey='id'
                  labelKey='vt_name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Actividad'
                  endpoint={`${GlobalVariables.getURL()}listVisitTypes`}
                  dataPath='data'
                  idKey='id'
                  labelKey='vt_name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
      {
        field: 'tech_name',
        headerName: 'Técnico asignado',
        minWidth: 160,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Técnico asignado'
                  endpoint={`${GlobalVariables.getURL()}listTechs`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Técnico asignado'
                  endpoint={`${GlobalVariables.getURL()}listTechs`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Técnico asignado'
                  endpoint={`${GlobalVariables.getURL()}listTechs`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
      {
        field: 'assigned_to',
        headerName: 'Asignado a',
        minWidth: 140,
        filterable: true,
        type: 'string',
        filterOperators: getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'rejectedReason',
        headerName: 'Motivo de rechazo',
        minWidth: 160,
        filterable: true,
        type: 'string',
        filterOperators: getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'job_start_date_manual',
        headerName: 'Fecha y hora de inicio de trabajos (técnico)',
        minWidth: 200,
        type: 'dateTime',
        filterable: true,
        valueGetter: ({ value }) => {
          if (!value) return null;
          let m = moment(value, 'YYYY-MM-DD HH:mm:ss', true);
          if (!m.isValid())
            m = moment(value, 'D [de] MMM. [de] YYYY HH:mm', true);
          if (!m.isValid()) m = moment(value); // fallback
          return m.isValid() ? m.toDate() : null;
        },
      },
      {
        field: 'job_end_date_manual',
        headerName: 'Fecha y hora de cierre (técnico)',
        minWidth: 200,
        type: 'dateTime',
        filterable: true,
        valueGetter: ({ value }) => {
          if (!value) return null;

          let m = moment(value, 'YYYY-MM-DD HH:mm:ss', true);

          if (!m.isValid())
            m = moment(value, 'D [de] MMM. [de] YYYY HH:mm', true);

          if (!m.isValid()) m = moment(value);

          return m.isValid() ? m.toDate() : 'null';
        },
      },
      // {
      //   field: 'job_start_date',
      //   headerName: 'Fecha y hora de inicio de trabajos',
      //   minWidth: 180,
      //   filterable: true,
      //   type: 'date',
      //   valueGetter: ({ value }) => {
      //     const momentDate = moment(value, 'D [de] MMM. [de] YYYY HH:mm');
      //     return momentDate.toDate();
      //   },
      //   renderCell: (params) => params.row.job_start_date,
      //   filterOperators: [
      //     ...getGridDateOperators(),
      //     {
      //       value: 'between',
      //       label: 'entre',
      //       InputComponent: ({ item, applyValue }) => {
      //         const [dateRanges, setDateRanges] = useState(
      //           item.value
      //             ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
      //             : [null, null]
      //         );
      //         const handleRangeChange = (newValue) => {
      //           const formattedRange = newValue.map((date) =>
      //             date ? moment(date).format('YYYY-MM-DD') : null
      //           );
      //           setDateRanges(formattedRange);
      //           applyValue({ ...item, value: formattedRange });
      //         };
      //         return (
      //           <LocalizationProvider
      //             dateAdapter={AdapterMoment}
      //             adapterLocale={moment.locale()}
      //           >
      //             <DateRangePicker
      //               label="Seleccionar rango de fechas"
      //               value={dateRanges.map((date) =>
      //                 date ? moment(date) : null
      //               )}
      //               onChange={handleRangeChange}
      //               localeText={{ start: 'Inicio', end: 'Fin' }}
      //               slotProps={{ textField: { variant: 'standard' } }}
      //             />
      //           </LocalizationProvider>
      //         );
      //       },
      //     },
      //   ],
      // },
      {
        field: 'job_end_date',
        headerName: 'Fecha y hora de cierre (bitacora)',
        minWidth: 180,
        filterable: true,
        type: 'date',
        valueGetter: ({ value }) => {
          const momentDate = moment(value, 'D [de] MMM. [de] YYYY HH:mm');
          return momentDate.toDate();
        },
        renderCell: (params) => params.row.job_end_date,
        filterOperators: [
          ...getGridDateOperators(),
          {
            value: 'between',
            label: 'entre',
            InputComponent: ({ item, applyValue }) => {
              const [dateRanges, setDateRanges] = useState(
                item.value
                  ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                  : [null, null]
              );
              const handleRangeChange = (newValue) => {
                const formattedRange = newValue.map((date) =>
                  date ? moment(date).format('YYYY-MM-DD') : null
                );
                setDateRanges(formattedRange);
                applyValue({ ...item, value: formattedRange });
              };
              return (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={moment.locale()}
                >
                  <DateRangePicker
                    label='Seleccionar rango de fechas'
                    value={dateRanges.map((date) =>
                      date ? moment(date) : null
                    )}
                    onChange={handleRangeChange}
                    localeText={{ start: 'Inicio', end: 'Fin' }}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />
                </LocalizationProvider>
              );
            },
          },
        ],
      },
      {
        field: 'resolution_time',
        headerName: 'Tiempo de resolución',
        minWidth: 140,
        filterable: false,
      },
      {
        field: 'close_comment',
        headerName: 'Comentario de cierre',
        minWidth: 200,
        filterable: true,
        type: 'string',
        filterOperators: getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'failure_note',
        headerName: 'Comentario del servicio',
        minWidth: 200,
        filterable: true,
        type: 'string',
        filterOperators: getGridStringOperators().filter(
          (operator) =>
            operator.value !== 'startsWith' &&
            operator.value !== 'endsWith' &&
            operator.value !== 'isAnyOf'
        ),
      },
      {
        field: 'created_at',
        headerName: createdAtString,
        minWidth: 180,
        filterable: true,
        type: 'dateTime',
        valueGetter: ({ value }) => {
          const momentDate = moment(value, 'D [de] MMM. [de] YYYY HH:mm');
          return momentDate.toDate();
        },
        renderCell: (params) => params.row.created_at,
        filterOperators: [
          ...getGridDateOperators(),
          {
            value: 'between',
            label: 'entre',
            InputComponent: ({ item, applyValue }) => {
              const [dateRanges, setDateRanges] = useState(
                item.value
                  ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                  : [null, null]
              );
              const handleRangeChange = (newValue) => {
                const formattedRange = newValue.map((date) =>
                  date ? moment(date).format('YYYY-MM-DD') : null
                );
                setDateRanges(formattedRange);
                applyValue({ ...item, value: formattedRange });
              };
              return (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={moment.locale()}
                >
                  <DateRangePicker
                    label='Seleccionar rango de fechas'
                    value={dateRanges.map((date) =>
                      date ? moment(date) : null
                    )}
                    onChange={handleRangeChange}
                    localeText={{ start: 'Inicio', end: 'Fin' }}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />
                </LocalizationProvider>
              );
            },
          },
        ],
      },
      {
        field: 'created_by_name',
        headerName: createdByString,
        minWidth: 140,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Creado Por'
                  endpoint={`${GlobalVariables.getURL()}listAll`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Creado Por'
                  endpoint={`${GlobalVariables.getURL()}listAll`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Creado Por'
                  endpoint={`${GlobalVariables.getURL()}listAll`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
      {
        field: 'updated_at',
        headerName: updatedAtString,
        minWidth: 180,
        filterable: true,
        type: 'dateTime',
        valueGetter: ({ value }) => {
          const momentDate = moment(value, 'D [de] MMM. [de] YYYY HH:mm');
          return momentDate.toDate();
        },
        renderCell: (params) => params.row.updated_at,
        filterOperators: [
          ...getGridDateOperators(),
          {
            value: 'between',
            label: 'entre',
            InputComponent: ({ item, applyValue }) => {
              const [dateRanges, setDateRanges] = useState(
                item.value
                  ? item.value.map((date) => moment(date).format('YYYY-MM-DD'))
                  : [null, null]
              );
              const handleRangeChange = (newValue) => {
                const formattedRange = newValue.map((date) =>
                  date ? moment(date).format('YYYY-MM-DD') : null
                );
                setDateRanges(formattedRange);
                applyValue({ ...item, value: formattedRange });
              };
              return (
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={moment.locale()}
                >
                  <DateRangePicker
                    label='Seleccionar rango de fechas'
                    value={dateRanges.map((date) =>
                      date ? moment(date) : null
                    )}
                    onChange={handleRangeChange}
                    localeText={{ start: 'Inicio', end: 'Fin' }}
                    slotProps={{ textField: { variant: 'standard' } }}
                  />
                </LocalizationProvider>
              );
            },
          },
        ],
      },
      {
        field: 'updated_by_name',
        headerName: updatedByString,
        minWidth: 140,
        filterable: true,
        filterOperators: [
          {
            value: 'equals',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Modificado Por'
                  endpoint={`${GlobalVariables.getURL()}listAll`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                />
              );
            },
          },
          {
            value: 'not',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Modificado Por'
                  endpoint={`${GlobalVariables.getURL()}listAll`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                />
              );
            },
          },
          {
            value: 'isAnyOf',
            InputComponent: (props) => {
              return (
                <AutocompleteFilterInput
                  {...props}
                  label='Modificado Por'
                  endpoint={`${GlobalVariables.getURL()}listAll`}
                  dataPath='data'
                  idKey='id'
                  labelKey='full_name'
                  isMultiple
                />
              );
            },
          },
          { value: 'isEmpty' },
          { value: 'isNotEmpty' },
        ],
      },
    ],
    [createdAtString, createdByString, updatedAtString, updatedByString]
  );

  // Aplicar un minWidth uniforme sin cálculos dinámicos
  const finalColumns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        minWidth: col.minWidth ?? LOCAL_COLUMN_WIDTH,
      })),
    [baseColumns]
  );

  const roleColumns = useMemo(() => {
    if (currUserRol === '1023') {
      const map = new Map(finalColumns.map((c) => [c.field, c]));
      return ROLE_1023_FIELDS_ORDER.map((f) => map.get(f)).filter(Boolean);
    }
    return finalColumns;
  }, [currUserRol, finalColumns]);
  //#endregion

  //#region render
  return (
    <Box sx={{ overflow: 'scroll', height: '87vh' }}>
      <div
        className='dt-btn-main-container'
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <span className='dabiWebText'>{visitsCRUDStr}</span>

        <button
          className='add-button create-visit-tour'
          onClick={() => setModalVShow(true)}
        >
          {addString} <FontAwesomeIcon icon={faAdd} />
        </button>

        <button
          disabled={isButtonDisabled}
          onClick={() => {
            setModalVMultipleDeleteShow(true);
            manageVisitMultipleDelete();
          }}
          className='multiple-delete-btn'
        >
          {multDeleteString}
        </button>
        <Tooltip title='Recargar datos'>
          <IconButton
            onClick={getData}
            disabled={isLoading}
            size='medium'
            sx={{ ml: 1 }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <div
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
        >
          <Tooltip title={viewTutorialString}>
            <IconButton
              className='tour-button'
              size='medium'
              onClick={() => restartTour()}
            >
              <FontAwesomeIcon icon={faCircleInfo} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <ModalV
        show={modalVShow}
        onHide={() => setModalVShow(false)}
        getVisits={getData}
        className='create-visit-form-tour'
      />

      <ModalVMultDelete
        show={modalVMultipleDeleteShow}
        onHide={() => setModalVMultipleDeleteShow(false)}
        getVisits={getData}
      />

      <ModalVDelete
        show={modalVDeleteShow}
        onHide={() => setModalVDeleteShow(false)}
        getVisits={getData}
      />

      <UpdateVisitModal
        visitId={visitId}
        showModal={showEditVisitModal}
        setShowModal={setShowEditVisitModal}
        refreshData={getData}
      />

      <SendVisitModal
        visitId={visitId}
        showModal={showSendVModal}
        setShowModal={setShowSendVModal}
        refreshData={getData}
      />

      <EditReportModal
        visitId={visitId}
        showModal={showEditReportModal}
        setShowModal={setShowEditReportModal}
      />

      <Box mt={2}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Chip
            label='Reenviados'
            sx={{
              backgroundColor: '#fff4dd',
              color: '#663c00',
              fontWeight: 'bold',
              border: '1px solid #e0c68a',
            }}
            size='small'
          />
          <Typography variant='body2' color='textSecondary'>
            Las filas con este color indican tickets reenviados
          </Typography>
        </Stack>
      </Box>

      <DataGridPro
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        apiRef={apiRef}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pagination
        paginationMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageModelChange}
        rowCount={rowCount}
        pageSizeOptions={[25, 50, 100, 500]}
        filterMode='server'
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        loading={isLoading}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        onDetailPanelExpandedRowIdsChange={(ids) => {
          if (ids.length > 1) {
            ids.slice(0, -1).forEach((id) => {
              apiRef.current.toggleDetailPanel(id);
            });
          }
        }}
        initialState={{
          columns: {
            orderedFields:
              currUserRol === '1023'
                ? ROLE_1023_FIELDS_ORDER // CHANGED: in read-only ignored columnOrder
                : columnOrder.length
                ? columnOrder
                : undefined,
            dimensions: columnSizes,
          },
          pinnedColumns: { right: ['action'] },
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => {
          setColumnVisibilityModel(newModel);
        }}
        rows={visitData || []}
        columns={roleColumns.concat(actionColumn)}
        pageSize={5}
        rowsPerPageOptions={[5]}
        checkboxSelection
        showQuickFilter
        disableRowSelectionOnClick
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
          if (newRowSelectionModel.length >= 1) {
            setIsButtonDisabled(false);
          }
          if (newRowSelectionModel.length === 0) {
            setIsButtonDisabled(true);
          }
        }}
        rowSelectionModel={rowSelectionModel}
        sx={{
          '& .Mui-row-reenviado': (theme) => ({
            backgroundColor: '#fff4dd',
            color: '#663c00',
            '&:hover': {
              backgroundColor: '#fff4dd', // Same as base to prevent hover effect
            },
          }),
        }}
        getRowClassName={(params) =>
          params.row.final_status_name === 'Reenviado'
            ? 'Mui-row-reenviado'
            : ''
        }
        slots={{
          noRowsOverlay: () => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              {noVisitsString}
            </Stack>
          ),
          toolbar: () => (
            <GridToolbarContainer
              sx={{ justifyContent: 'space-between', display: 'flex' }}
            >
              <div>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
                <Button
                  onClick={handleExport}
                  variant='text'
                  color='primary'
                  size='small'
                  disabled={isExporting}
                  startIcon={
                    isExporting ? (
                      <CircularProgress size={16} color='inherit' />
                    ) : (
                      <svg
                        focusable='false'
                        aria-hidden='true'
                        width={24}
                        height={24}
                        viewBox='0 0 24 24'
                        style={{ fill: '#003087' }}
                      >
                        <path d='M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z'></path>
                      </svg>
                    )
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    color: 'primary.main',
                    '& .MuiButton-startIcon': { mr: 1 },
                    minWidth: 0,
                    paddingX: 1,
                    paddingY: 0.5,
                  }}
                >
                  {isExporting ? 'Exportando...' : 'Exportar'}
                </Button>
              </div>
              <GridToolbarQuickFilter debounceMs={2000} />
            </GridToolbarContainer>
          ),
        }}
        slotProps={{
          filterPanel: {
            filterFormProps: { filterColumns },
            getColumnForNewFilter,
          },
          toolbar: { showQuickFilter: true },
        }}
        className='visits-datagrid-tour'
      />

      <Dialog open={showReportViewer} onClose={closeReportViewer} maxWidth='md'>
        <DialogContent
          sx={{
            width: '100% !important',
            height: '70% !important',
          }}
        >
          <ReportViewer
            template={reportTemplate}
            inputs={reportInputs}
            onClose={closeReportViewer}
            downloadReport={() => {
              downloadReport(visitId, false);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
//#endregion
